import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ReferredUsersType } from '../../../../types/types';
import { StyledDataGrid } from '../../../../constants/styling';
import { useNetwork } from '../../../../utils/network';
import { panelSx, PT } from '../../../../components/portalUi';

// Dark-teal stat tile (replaces the old flat-grey boxes).
const statLabelSx = {
  font: '800 11px/1 Montserrat, sans-serif',
  letterSpacing: '0.16em',
  textTransform: 'uppercase' as const,
  color: PT.textMuted
};
const statValueSx = {
  mt: 1.25,
  fontWeight: 800,
  fontSize: 34,
  lineHeight: 1,
  color: PT.teal
};

type SettingsUserReferralsProps = {
  isSmallSize?: boolean;
};

interface referralCommissionsUserData {
  id: number;
  username: string;
}

interface ReferralClaim {
  commissionAmount: string;
  createdAt: string;
  description: string;
  id: number;
  referralCommissionsUserReferred: referralCommissionsUserData;
  referredUserId: number;
  referrerId: number;
  status: string;
  tokenAmount: string;
  transactionId: number;
  updatedAt: string;
}

export const SettingsUserReferrals = ({
  isSmallSize
}: SettingsUserReferralsProps) => {
  const { get } = useNetwork();
  const [listOfUsersReferred, setListOfUsersReferred] = useState<
    ReferredUsersType[]
  >([]);
  const [amountEarned, setAmountEarned] = useState(0);
  const [referralClaims, setReferralClaims] = useState<ReferralClaim[]>([]);

  useEffect(() => {
    const fetchReferredUsers = async () => {
      try {
        const data = await get('/user/referrals', true);
        setListOfUsersReferred(data.referrals);
        setAmountEarned(data.earnt);
      } catch (error) {
        console.error('Failed to fetch referred users:', error);
      }
    };

    const fetchTotalReferredAmount = async () => {
      try {
        const data = await get(
          '/referral-commission/get-all-referral-data',
          true
        );
        setReferralClaims(data);
      } catch (error) {
        console.error('Failed to fetch referred users:', error);
      }
    };

    fetchReferredUsers();
    fetchTotalReferredAmount();
  }, []);

  const referredUsersTableColumns = [
    { headerName: 'Username', field: 'username', flex: 1 },
    { headerName: 'Date Joined', field: 'joinDate', flex: 1 }
  ];

  const calculateTotalTokens = (data: ReferralClaim[]) => {
    let count = 0;
    data.forEach((referral) => {
      count = count + parseInt(referral.tokenAmount);
    });

    return (count / 100000).toFixed(5);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ pb: '1.5rem' }}>
        {/* <Typography variant="h6" textAlign="center" fontWeight="medium">
          Your referral earnings increase as you refer more users. Each milestone reached boosts your earnings multiplier:
        </Typography>
        <Typography variant="body2" textAlign="center" sx={{ mt: 1 }}>
          <strong>Milestones:</strong>
          <br />10+ referrals = 2x, 20+ referrals = 3x, 30+ referrals = 4x, 40+ referrals = 5x, 50+ referrals = 6x
        </Typography> */}
        <Typography variant="body1" textAlign="center" sx={{ mt: 2 }}>
          <strong>Current Multiplier:</strong>{' '}
          {listOfUsersReferred.length >= 50
            ? 6
            : listOfUsersReferred.length >= 40
              ? 5
              : listOfUsersReferred.length >= 30
                ? 4
                : listOfUsersReferred.length >= 20
                  ? 3
                  : listOfUsersReferred.length >= 10
                    ? 2
                    : 1}
        </Typography>
      </Box>
      <Box
        sx={{
          pb: '2rem',
          display: 'flex',
          flexDirection: isSmallSize ? 'column' : 'row',
          gap: '2rem'
        }}
      >
        <Box
          sx={{
            ...panelSx,
            p: 2.5,
            width: isSmallSize ? '100%' : '48%',
            textAlign: 'center'
          }}
        >
          <Typography sx={statLabelSx}>Users Referred</Typography>
          <Typography sx={statValueSx}>{listOfUsersReferred.length}</Typography>
        </Box>

        <Box
          sx={{
            ...panelSx,
            p: 2.5,
            width: isSmallSize ? '100%' : '48%',
            textAlign: 'center'
          }}
        >
          <Typography sx={statLabelSx}>Amount Earned (INC)</Typography>
          <Typography sx={statValueSx}>
            {referralClaims.length > 0
              ? calculateTotalTokens(referralClaims)
              : 0}
          </Typography>
        </Box>
      </Box>

      {listOfUsersReferred.length > 0 && (
        <Box>
          <StyledDataGrid
            rows={listOfUsersReferred.map((data, index) => {
              return {
                id: index,
                username: data.username,
                joinDate: data.joinDate
              };
            })}
            columns={referredUsersTableColumns}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 5 }
              }
            }}
            disableRowSelectionOnClick
          />
        </Box>
      )}
    </Box>
  );
};
